/**
 * Service d'export
 *
 * Gère l'export des données du dashboard en différents formats :
 * - PDF (capture visuelle du dashboard)
 * - Excel (données détaillées)
 * - CSV (format simple)
 */

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CATEGORY_LABELS } from '../utils/constants';

/**
 * Convertit du HTML en texte simple pour le PDF
 * @param {string} html - Texte HTML
 * @returns {string} - Texte sans HTML
 */
const htmlToPlainText = (html) => {
  if (!html) return '';

  // Créer un élément temporaire pour parser le HTML
  const temp = document.createElement('div');
  temp.innerHTML = html;

  // Remplacer certains éléments par des équivalents texte
  temp.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
  temp.querySelectorAll('p').forEach(p => {
    p.insertAdjacentText('afterend', '\n');
  });
  temp.querySelectorAll('li').forEach(li => {
    li.insertAdjacentText('beforebegin', '• ');
    li.insertAdjacentText('afterend', '\n');
  });
  temp.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(h => {
    h.insertAdjacentText('beforebegin', '\n');
    h.insertAdjacentText('afterend', '\n');
  });

  // Récupérer le texte
  let text = temp.textContent || temp.innerText || '';

  // Nettoyer les espaces multiples et lignes vides
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 lignes vides
  text = text.trim();

  return text;
};

/**
 * Exporte le dashboard en PDF structuré
 * @param {Array} userStories - Liste des user stories
 * @param {Array} epics - Liste des epics avec progression
 * @param {Array} milestones - Liste des jalons
 * @param {string} clientName - Nom du client
 * @param {string} projectName - Nom du projet
 * @param {Object} options - Options de personnalisation (optionnel)
 * @param {Object} options.includeSections - Sections à inclure
 * @param {boolean} options.includeSections.statistics - Inclure les statistiques
 * @param {boolean} options.includeSections.userStories - Inclure les user stories
 * @param {boolean} options.includeSections.milestones - Inclure les jalons
 * @returns {Promise<void>}
 */
export const exportToPDF = async (userStories, epics, milestones, clientName, projectName, options = null) => {
  try {
    // Déterminer quelles sections inclure
    const includeStatistics = !options || options.includeSections?.statistics !== false;
    const includeUserStories = !options || options.includeSections?.userStories !== false;
    const includeMilestones = !options || options.includeSections?.milestones !== false;

    // Validation des données
    if (!userStories || !Array.isArray(userStories)) {
      throw new Error('Les données des user stories sont invalides');
    }

    // Valeurs par défaut
    const validEpics = epics && Array.isArray(epics) ? epics : [];
    const validMilestones = milestones && Array.isArray(milestones) ? milestones : [];

    console.log('Export PDF - Données:', {
      userStoriesCount: userStories.length,
      epicsCount: validEpics.length,
      milestonesCount: validMilestones.length
    });

    // Créer le PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPosition = 20;

    // ===== PAGE DE GARDE =====
    pdf.setFontSize(24);
    pdf.setTextColor(37, 99, 235); // Bleu
    pdf.text('Rapport de Projet', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 15;
    pdf.setFontSize(18);
    pdf.setTextColor(23, 23, 23);
    pdf.text(projectName, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 12;
    pdf.setFontSize(12);
    pdf.setTextColor(115, 115, 115);
    pdf.text(`Votre organisation : ${clientName}`, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 8;
    pdf.setFontSize(10);
    pdf.text(`Généré le: ${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}`, pageWidth / 2, yPosition, { align: 'center' });

    // Info sur le filtrage par responsable de jalon
    if (validMilestones.length > 0) {
      yPosition += 6;
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      const responsables = [...new Set(validMilestones.map(m => m.responsableName).filter(Boolean))];
      if (responsables.length > 0) {
        pdf.text(`Jalons filtrés par responsable: ${responsables.join(', ')}`, pageWidth / 2, yPosition, { align: 'center' });
      }
    }

    yPosition += 20;

    // ===== STATISTIQUES GLOBALES =====
    if (includeStatistics) {
      pdf.setFontSize(16);
      pdf.setTextColor(23, 23, 23);
      pdf.text('Statistiques Globales', 14, yPosition);
      yPosition += 10;

      const stats = calculateStats(userStories, validEpics, validMilestones);

      autoTable(pdf, {
        startY: yPosition,
        head: [['Métrique', 'Valeur']],
        body: [
          ['Total User Stories', stats.totalTasks],
          ['Backlog', stats.backlogTasks],
          ['En Test', stats.testingTasks],
          ['Tests à valider', stats.clientTestingTasks],
          ['Terminées', `${stats.doneTasks} (${stats.completionRate}%)`],
          ['Total Story Points', `${stats.totalStoryPoints} SP`],
          ['Temps Total Estimé', `${stats.totalHours}h (${(stats.totalHours / 8).toFixed(1)} jours)`],
          ['Total Jalons', stats.totalMilestones],
          ['Jalons Atteints', stats.milestonesReached]
        ],
        theme: 'grid',
        headStyles: { fillColor: [37, 99, 235], textColor: 255, fontSize: 11, fontStyle: 'bold' },
        bodyStyles: { fontSize: 10 },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 80 },
          1: { halign: 'right', cellWidth: 'auto' }
        },
        margin: { left: 14, right: 14 }
      });

      yPosition = pdf.lastAutoTable.finalY + 15;
    }

    // ===== USER STORIES PAR CATÉGORIE =====
    if (includeUserStories && userStories.length > 0) {
      // Nouvelle page pour commencer la liste des US
      pdf.addPage();
      yPosition = 20;

      pdf.setFontSize(16);
      pdf.text('User Stories par Catégorie', 14, yPosition);
      yPosition += 10;

      // Ordre des catégories : Backlog → En Test → Tests à valider → Done
      const categories = [
        { key: 'Backlog', label: 'Backlog', color: [107, 114, 128] },
        { key: 'En Test', label: 'En Test', color: [59, 130, 246] },
        { key: 'Tests à valider', label: 'Tests à valider', color: [249, 115, 22] },
        { key: 'Done', label: 'Done', color: [16, 185, 129] }
      ];

      for (const category of categories) {
        const categoryStories = userStories.filter(us => us.category === category.key);

        if (categoryStories.length === 0) continue;

        // Nouvelle page pour chaque catégorie si nécessaire
        if (yPosition > 250) {
          pdf.addPage();
          yPosition = 20;
        }

        // En-tête de catégorie avec totaux de SP et heures sur la même ligne
        const categoryStoryPoints = categoryStories.reduce((sum, us) => sum + (us.storyPoints || 0), 0);
        const categoryHours = categoryStories.reduce((sum, us) => sum + (us.hourlyRate || 0), 0);

        pdf.setFontSize(14);
        pdf.setTextColor(37, 99, 235);

        let headerText = `${category.label} (${categoryStories.length} user stor${categoryStories.length > 1 ? 'ies' : 'y'}`;
        if (categoryStoryPoints > 0) {
          headerText += ` • ${categoryStoryPoints} SP • ${categoryHours}h`;
        }
        headerText += ')';

        pdf.text(headerText, 14, yPosition);
        yPosition += 8;

        // Préparer les données du tableau
        const tableData = categoryStories.map((us, index) => {
          const usCode = us.code || `US-${String(index + 1).padStart(2, '0')}`;
          const usName = us.name || 'Sans titre';

          // Description : convertir HTML et limiter la longueur
          let description = '-';
          if (us.description) {
            const plainText = htmlToPlainText(us.description);
            // Limiter à 150 caractères pour le tableau
            description = plainText.length > 150
              ? plainText.substring(0, 147) + '...'
              : plainText;
          }

          const storyPoints = us.storyPoints && us.storyPoints > 0 ? `${us.storyPoints} SP` : '-';
          const hours = us.hourlyRate && us.hourlyRate > 0 ? `${us.hourlyRate}h` : '-';

          return [usCode, usName, description, storyPoints, hours];
        });

        // Afficher le tableau des User Stories
        autoTable(pdf, {
          startY: yPosition,
          head: [['Code', 'Nom', 'Description', 'SP', 'Heures']],
          body: tableData,
          theme: 'grid',
          headStyles: {
            fillColor: category.color,
            textColor: 255,
            fontSize: 9,
            fontStyle: 'bold'
          },
          bodyStyles: {
            fontSize: 8,
            cellPadding: 3
          },
          columnStyles: {
            0: { cellWidth: 20, fontStyle: 'bold' },  // Code
            1: { cellWidth: 50 },                      // Nom
            2: { cellWidth: 75 },                      // Description
            3: { cellWidth: 15, halign: 'center' },    // SP
            4: { cellWidth: 18, halign: 'center' }     // Heures
          },
          margin: { left: 14, right: 14 },
          // eslint-disable-next-line no-loop-func
          didDrawPage: function(data) {
            // Mettre à jour yPosition après chaque page
            yPosition = data.cursor.y;
          }
        });

        yPosition = pdf.lastAutoTable.finalY + 8; // Espace après chaque catégorie
      }
    }

    // ===== JALONS =====
    // Section Epics supprimée
    if (includeMilestones && validMilestones.length > 0) {
      if (yPosition > 200) {
        pdf.addPage();
        yPosition = 20;
      }

      pdf.setFontSize(16);
      pdf.setTextColor(23, 23, 23);
      pdf.text('Jalons du Projet', 14, yPosition);
      yPosition += 10;

      const milestonesData = validMilestones.map(milestone => {
        // Gérer les dates invalides
        let deadlineStr = '-';
        let reachedDateStr = '-';

        try {
          if (milestone.deadline) {
            const deadlineDate = new Date(milestone.deadline);
            deadlineStr = !isNaN(deadlineDate.getTime()) ? format(deadlineDate, 'dd/MM/yyyy') : 'Date invalide';
          }
        } catch (e) {
          deadlineStr = 'Date invalide';
        }

        try {
          if (milestone.reached_date) {
            const reachedDate = new Date(milestone.reached_date);
            reachedDateStr = !isNaN(reachedDate.getTime()) ? format(reachedDate, 'dd/MM/yyyy') : '-';
          }
        } catch (e) {
          reachedDateStr = '-';
        }

        return [
          milestone.name || 'Sans nom',
          milestone.responsableName || '-',
          deadlineStr,
          milestone.is_reached || milestone.isReached ? 'Atteint' : 'En cours',
          reachedDateStr,
          milestone.daysRemaining !== null && !(milestone.is_reached || milestone.isReached) ? `${milestone.daysRemaining}j` : '-'
        ];
      });

      autoTable(pdf, {
        startY: yPosition,
        head: [['Nom', 'Responsable', 'Date limite', 'Statut', 'Date atteinte', 'Jours restants']],
        body: milestonesData,
        theme: 'grid',
        headStyles: { fillColor: [249, 115, 22], textColor: 255, fontSize: 9, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 40 },
          2: { halign: 'center', cellWidth: 25 },
          3: { halign: 'center', cellWidth: 22 },
          4: { halign: 'center', cellWidth: 25 },
          5: { halign: 'center', cellWidth: 20 }
        },
        margin: { left: 14, right: 14 },
        didParseCell: function(data) {
          // Colorer la colonne statut (maintenant à l'index 3 au lieu de 2)
          if (data.column.index === 3 && data.section === 'body') {
            if (data.cell.raw === 'Atteint') {
              data.cell.styles.textColor = [16, 185, 129];
              data.cell.styles.fontStyle = 'bold';
            } else {
              data.cell.styles.textColor = [59, 130, 246];
            }
          }
        }
      });
    }

    // ===== PIED DE PAGE =====
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(115, 115, 115);
      pdf.text(
        `Page ${i} sur ${pageCount}`,
        pageWidth / 2,
        pdf.internal.pageSize.getHeight() - 10,
        { align: 'center' }
      );
    }

    // Télécharger le PDF
    const fileName = `rapport_${projectName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    console.error('Erreur détaillée lors de l\'export PDF:', error);
    console.error('Stack trace:', error.stack);
    throw new Error(`Échec de l'export PDF: ${error.message}`);
  }
};

/**
 * Calculer les statistiques globales
 */
const calculateStats = (userStories, epics, milestones) => {
  const totalTasks = userStories.length;
  const backlogTasks = userStories.filter(us => us.category === 'Backlog').length;
  const testingTasks = userStories.filter(us => us.category === 'En Test').length;
  const clientTestingTasks = userStories.filter(us => us.category === 'Tests à valider' || us.category === 'Test Client').length;
  const doneTasks = userStories.filter(us => us.category === 'Done').length;
  const completionRate = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const totalMilestones = milestones ? milestones.length : 0;
  const milestonesReached = milestones ? milestones.filter(m => m.is_reached || m.isReached).length : 0;

  // Calculer les totaux de story points et heures
  const totalStoryPoints = userStories.reduce((sum, us) => sum + (us.storyPoints || 0), 0);
  const totalHours = userStories.reduce((sum, us) => sum + (us.hourlyRate || 0), 0);

  return {
    totalTasks,
    backlogTasks,
    testingTasks,
    clientTestingTasks,
    doneTasks,
    completionRate,
    totalMilestones,
    milestonesReached,
    totalStoryPoints,
    totalHours
  };
};


/**
 * Exporte les données en Excel
 * @param {Array} userStories - Liste des user stories
 * @param {Array} epics - Liste des epics avec progression
 * @param {Array} milestones - Liste des jalons
 * @param {string} projectName - Nom du projet
 * @returns {void}
 */
export const exportToExcel = (userStories, epics, milestones, projectName) => {
  try {
    // Créer un nouveau workbook
    const wb = XLSX.utils.book_new();

    // Feuille 1: User Stories (Epic supprimé)
    const userStoriesData = userStories.map((task, index) => ({
      'Numéro': task.code || `US-${String(index + 1).padStart(2, '0')}`,
      'Date de création': task.create_date ? format(new Date(task.create_date), 'dd/MM/yyyy') : '',
      'Nom': task.name,
      'Description': task.description ? htmlToPlainText(task.description) : '',
      'Catégorie': CATEGORY_LABELS[task.category] || task.category,
      'Story Points': task.storyPoints || 0,
      'Durée (h)': task.hourlyRate || 0,
      'Priorité': task.priority !== undefined && task.priority !== null ? task.priority : ''
    }));

    const wsUserStories = XLSX.utils.json_to_sheet(userStoriesData);

    // Définir la largeur des colonnes (Epic supprimé)
    wsUserStories['!cols'] = [
      { wch: 12 }, // Numéro
      { wch: 15 }, // Date de création
      { wch: 40 }, // Nom
      { wch: 60 }, // Description
      { wch: 15 }, // Catégorie
      { wch: 12 }, // Story Points
      { wch: 12 }, // Durée (h)
      { wch: 10 }  // Priorité
    ];

    XLSX.utils.book_append_sheet(wb, wsUserStories, 'User Stories');

    // Feuille 2: Jalons (section Epics supprimée)
    if (milestones && milestones.length > 0) {
      const milestonesData = milestones.map(milestone => ({
        'Nom': milestone.name,
        'Responsable': milestone.responsableName || '-',
        'Date limite': milestone.deadline ? format(new Date(milestone.deadline), 'dd/MM/yyyy') : '',
        'Statut': milestone.is_reached || milestone.isReached ? 'Atteint' : 'En cours',
        'Date atteinte': milestone.reached_date ? format(new Date(milestone.reached_date), 'dd/MM/yyyy') : '',
        'Tâches associées': milestone.taskCount || milestone.taskIds?.length || 0,
        'Jours restants': milestone.daysRemaining !== null ? milestone.daysRemaining : ''
      }));

      const wsMilestones = XLSX.utils.json_to_sheet(milestonesData);
      wsMilestones['!cols'] = [
        { wch: 30 }, // Nom
        { wch: 25 }, // Responsable
        { wch: 15 }, // Date limite
        { wch: 12 }, // Statut
        { wch: 15 }, // Date atteinte
        { wch: 18 }, // Tâches associées
        { wch: 15 }  // Jours restants
      ];

      XLSX.utils.book_append_sheet(wb, wsMilestones, 'Jalons');
    }

    // Générer et télécharger le fichier
    const fileName = `export_${projectName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
    XLSX.writeFile(wb, fileName);

  } catch (error) {
    console.error('Erreur lors de l\'export Excel:', error);
    throw new Error('Échec de l\'export Excel. Veuillez réessayer.');
  }
};

/**
 * Exporte les user stories en CSV
 * @param {Array} userStories - Liste des user stories
 * @param {string} projectName - Nom du projet
 * @returns {void}
 */
export const exportToCSV = (userStories, projectName) => {
  try {
    // Préparer les en-têtes (Epic supprimé)
    const headers = [
      'Numéro',
      'Date de création',
      'Nom',
      'Description',
      'Catégorie',
      'Story Points',
      'Durée (h)',
      'Priorité'
    ];

    // Préparer les lignes de données (Epic supprimé)
    const rows = userStories.map((task, index) => [
      task.code || `US-${String(index + 1).padStart(2, '0')}`,
      task.create_date ? format(new Date(task.create_date), 'dd/MM/yyyy') : '',
      `"${(task.name || '').replace(/"/g, '""')}"`, // Échapper les guillemets
      `"${(task.description ? htmlToPlainText(task.description) : '').replace(/"/g, '""')}"`,
      CATEGORY_LABELS[task.category] || task.category,
      task.storyPoints || 0,
      task.hourlyRate || 0,
      task.priority !== undefined && task.priority !== null ? task.priority : ''
    ]);

    // Construire le CSV
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Créer le blob et télécharger
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const fileName = `export_${projectName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('Erreur lors de l\'export CSV:', error);
    throw new Error('Échec de l\'export CSV. Veuillez réessayer.');
  }
};

/**
 * Exporte un rapport résumé en PDF
 * @param {Object} stats - Statistiques du projet
 * @param {string} clientName - Nom du client
 * @param {string} projectName - Nom du projet
 * @returns {void}
 */
export const exportSummaryReport = (stats, clientName, projectName) => {
  try {
    const pdf = new jsPDF();
    const pageWidth = pdf.internal.pageSize.getWidth();
    let yPosition = 20;

    // En-tête
    pdf.setFontSize(20);
    pdf.setTextColor(23, 23, 23);
    pdf.text('Rapport de Projet', pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 15;
    pdf.setFontSize(14);
    pdf.setTextColor(37, 99, 235); // Accent color
    pdf.text(projectName, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 10;
    pdf.setFontSize(10);
    pdf.setTextColor(115, 115, 115);
    pdf.text(`Votre organisation : ${clientName}`, pageWidth / 2, yPosition, { align: 'center' });

    yPosition += 5;
    pdf.text(`Généré le: ${format(new Date(), 'dd MMMM yyyy à HH:mm', { locale: fr })}`, pageWidth / 2, yPosition, { align: 'center' });

    // Ligne de séparation
    yPosition += 10;
    pdf.setDrawColor(229, 231, 235);
    pdf.line(20, yPosition, pageWidth - 20, yPosition);

    // Statistiques
    yPosition += 15;
    pdf.setFontSize(14);
    pdf.setTextColor(23, 23, 23);
    pdf.text('Statistiques Globales', 20, yPosition);

    yPosition += 10;
    pdf.setFontSize(11);
    pdf.setTextColor(55, 55, 55);

    const statLines = [
      `Tâches totales: ${stats.total}`,
      `Tâches terminées: ${stats.done} (${stats.completionRate}%)`,
      `En cours: ${stats.inProgress}`,
      `En test: ${stats.testing}`,
      `Tâches en retard: ${stats.overdueTasks}`
    ];

    statLines.forEach(line => {
      pdf.text(line, 25, yPosition);
      yPosition += 7;
    });

    // Télécharger
    const fileName = `rapport_${projectName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.pdf`;
    pdf.save(fileName);

  } catch (error) {
    console.error('Erreur lors de la génération du rapport:', error);
    throw new Error('Échec de la génération du rapport. Veuillez réessayer.');
  }
};

const exportService = {
  exportToPDF,
  exportToExcel,
  exportToCSV,
  exportSummaryReport
};

export default exportService;
